import { useState } from "react";
import { toast } from "sonner";
import API, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Textarea } from "../ui/textarea";
import { Sparkle, Siren, FirstAid, ArrowsClockwise, Warning, WarningOctagon, Wrench } from "@phosphor-icons/react";
import { URGENCY_LABEL } from "./oohUtils";

export default function OohTriageCard({ c, onChanged, onEscalate }) {
  const ai = c.ai_triage;
  const [busy, setBusy] = useState("");
  const [rerun, setRerun] = useState({ open: false, notes: "" });
  const [decide, setDecide] = useState({ open: false, urgency: "", reason: "" });
  const decided = c.urgency && c.urgency !== "unknown";

  const doRerun = async () => {
    setBusy("rerun");
    try {
      await API.post(`/ooh/cases/${c.id}/triage`, { notes: rerun.notes });
      toast.success("Triage refreshed with the answers so far");
      setRerun({ open: false, notes: "" });
      onChanged();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "AI triage failed"); }
    finally { setBusy(""); }
  };

  const doDecide = async () => {
    setBusy("decide");
    try {
      const { data } = await API.post(`/ooh/cases/${c.id}/urgency`, { urgency: decide.urgency, reason: decide.reason, raise_emergency: true });
      toast.success(decide.urgency === "urgent" ? `Emergency job ${data.emergency_job?.reference || ""} raised — find a contractor below` : "Marked non-urgent — walk the caller through the steps");
      setDecide({ open: false, urgency: "", reason: "" });
      onChanged();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't save the decision"); }
    finally { setBusy(""); }
  };

  return (
    <section className="rounded-md border border-border bg-card" data-testid="ooh-triage-card">
      <header className="flex items-center justify-between gap-2 px-4 py-2.5 border-b border-border/70">
        <h3 className="text-sm font-semibold flex items-center gap-2"><Sparkle size={16} weight="duotone" className="text-primary" /> AI triage</h3>
        <div className="flex items-center gap-2">
          {c.ai_status === "fallback" && <span className="text-[10px] text-amber-700 inline-flex items-center gap-1" data-testid="ooh-ai-fallback"><Warning size={11} /> AI unavailable — standard questions loaded</span>}
          <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setRerun({ open: true, notes: "" })} disabled={busy === "rerun" || c.ai_status === "running"} data-testid="ooh-triage-rerun">
            <ArrowsClockwise size={13} className={c.ai_status === "running" ? "animate-spin" : ""} /> {c.ai_status === "running" ? "Thinking…" : "Re-run with answers"}
          </Button>
        </div>
      </header>
      <div className="px-4 py-3 space-y-3">
        {!ai ? (
          <p className="text-sm text-muted-foreground animate-pulse" data-testid="ooh-ai-pending">Reading the request and writing the call script…</p>
        ) : (
          <>
            <p className="text-sm" data-testid="ooh-ai-summary">{ai.summary}</p>
            <div className={`rounded-md px-3 py-2 text-sm border ${ai.urgency === "urgent" ? "border-red-300 bg-red-50 text-red-900 dark:bg-red-950/30 dark:text-red-200" : "border-emerald-300 bg-emerald-50 text-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-200"}`} data-testid="ooh-ai-urgency">
              <span className="font-semibold uppercase tracking-wider">AI suggests {ai.urgency === "urgent" ? "URGENT — call-out" : "NON-URGENT — make safe, book for the day"}</span>
              <span className="ml-2 opacity-70">{Math.round((ai.confidence || 0) * 100)}% confident</span>
              {ai.urgency_reason && <p className="mt-1 opacity-90">{ai.urgency_reason}</p>}
            </div>
            {ai.safety_first?.length > 0 && (
              <div className="rounded-md border-2 border-red-500 bg-red-50 dark:bg-red-950/30 px-3 py-2" data-testid="ooh-safety-first">
                <p className="text-[11px] font-bold uppercase tracking-wider text-red-700 dark:text-red-300 flex items-center gap-1"><FirstAid size={13} weight="fill" /> Say this first</p>
                <ul className="mt-1 space-y-1 text-sm text-red-900 dark:text-red-100 list-disc pl-4">{ai.safety_first.map((s, i) => <li key={i}>{s}</li>)}</ul>
              </div>
            )}
            {ai.script_opening && <p className="text-sm italic text-muted-foreground border-l-2 border-primary/40 pl-3" data-testid="ooh-script-opening">“{ai.script_opening}”</p>}
            <div className="grid sm:grid-cols-2 gap-3 text-sm">
              {ai.escalate_if?.length > 0 && (
                <div>
                  <p className="font-semibold uppercase tracking-wider text-[10px] text-muted-foreground flex items-center gap-1"><WarningOctagon size={12} /> Escalate to a call-out if</p>
                  <ul className="mt-1 space-y-0.5 list-disc pl-4">{ai.escalate_if.map((s, i) => <li key={i}>{s}</li>)}</ul>
                </div>
              )}
              {ai.trades?.length > 0 && (
                <div>
                  <p className="font-semibold uppercase tracking-wider text-[10px] text-muted-foreground flex items-center gap-1"><Wrench size={12} /> Trades if it comes to a call-out</p>
                  <p className="mt-1">{ai.trades.join(", ")}</p>
                  {ai.morning_note && <p className="mt-2 text-muted-foreground">Day team: {ai.morning_note}</p>}
                </div>
              )}
            </div>
          </>
        )}

        <div className="pt-2 border-t border-border/60">
          {decided ? (
            <div className="flex flex-wrap items-center justify-between gap-2 text-xs" data-testid="ooh-decision">
              <span>Decision: <strong className={c.urgency === "urgent" ? "text-red-700" : "text-emerald-700"}>{URGENCY_LABEL[c.urgency]}</strong>{c.urgency_set_by ? ` by ${c.urgency_set_by}` : ""}{c.urgency_reason ? ` — ${c.urgency_reason}` : ""}</span>
              {c.status !== "closed" && c.status !== "handed_over" && c.urgency !== "urgent" && (
                <Button size="sm" variant="outline" className="h-7 text-xs border-red-400 text-red-700 hover:bg-red-50" onClick={onEscalate} data-testid="ooh-escalate-btn"><Siren size={13} /> Escalate to call-out</Button>
              )}
            </div>
          ) : c.status !== "closed" && (
            <div className="flex flex-wrap gap-2">
              <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={onEscalate} data-testid="ooh-decide-urgent"><Siren size={15} weight="fill" /> Urgent — escalate to a call-out (pick SLA & contractor)</Button>
              <Button variant="outline" className="border-emerald-500 text-emerald-700 hover:bg-emerald-50 dark:hover:bg-emerald-950/30" onClick={() => setDecide({ open: true, urgency: "non_urgent", reason: ai?.urgency === "non_urgent" ? ai.urgency_reason : "" })} data-testid="ooh-decide-non-urgent">Non-urgent — make safe tonight, book for the day</Button>
            </div>
          )}
        </div>
      </div>

      <Dialog open={rerun.open} onOpenChange={(o) => setRerun((r) => ({ ...r, open: o }))}>
        <DialogContent data-testid="ooh-rerun-dialog">
          <DialogHeader><DialogTitle>Re-run the AI triage</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">The answers you've recorded so far are included automatically. Add anything else the caller told you.</p>
          <Textarea rows={4} value={rerun.notes} onChange={(e) => setRerun((r) => ({ ...r, notes: e.target.value }))} placeholder="e.g. Caller has turned the stopcock off, drip has slowed to one every few seconds" data-testid="ooh-rerun-notes" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRerun({ open: false, notes: "" })}>Cancel</Button>
            <Button onClick={doRerun} disabled={busy === "rerun"} data-testid="ooh-rerun-confirm">{busy === "rerun" ? "Thinking…" : "Re-run triage"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={decide.open} onOpenChange={(o) => setDecide((d) => ({ ...d, open: o }))}>
        <DialogContent data-testid="ooh-decide-dialog">
          <DialogHeader><DialogTitle>{decide.urgency === "urgent" ? "Confirm: urgent call-out tonight" : "Confirm: non-urgent — make safe & hand over"}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            {decide.urgency === "urgent" ? "A Critical emergency job (4h SLA) is raised straight away with the triage notes attached, and the contractor finder opens so you can get someone on site."
              : "The case stays on the desk tonight with the self-help script. At 08:00 on the next working day it is handed to the day team as a job request carrying everything you recorded."}
          </p>
          <Textarea rows={3} value={decide.reason} onChange={(e) => setDecide((d) => ({ ...d, reason: e.target.value }))} placeholder="Why (goes on the report)" data-testid="ooh-decide-reason" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setDecide({ open: false, urgency: "", reason: "" })}>Cancel</Button>
            <Button className={decide.urgency === "urgent" ? "bg-red-600 hover:bg-red-700 text-white" : ""} onClick={doDecide} disabled={busy === "decide"} data-testid="ooh-decide-confirm">{busy === "decide" ? "Saving…" : decide.urgency === "urgent" ? "Raise emergency job" : "Confirm non-urgent"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
